import React, { useContext, useState, useEffect, useMemo } from 'react';
import { AppContext } from './App';
import axios from 'axios';
import { SERVER_URL } from './config';
import './Invites.css';

const Invites = () => {
  const { user } = useContext(AppContext);

  const [invites, setInvites] = useState([]);
  const [filteredInvites, setFilteredInvites] = useState([]);

  const [typeFilter, setTypeFilter] = useState('');
  const [arrivingFilter, setArrivingFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');

  const [searchQuery, setSearchQuery] = useState('');

  const [showEditModal, setShowEditModal] = useState(false);
  const [currentInvite, setCurrentInvite] = useState(null);

  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newArriving, setNewArriving] = useState('');
  const [newRelationship, setNewRelationship] = useState('');
  const [newBoysCount, setNewBoysCount] = useState('');
  const [newGirlsCount, setNewGirlsCount] = useState('');

  const [newPreferredLanguage, setNewPreferredLanguage] = useState('auto');

  const [copiedIndex, setCopiedIndex] = useState(null);
  const [editableFields, setEditableFields] = useState({});

  const [showSpecialsModal, setShowSpecialsModal] = useState(false);
  const [editSpecialsInvite, setEditSpecialsInvite] = useState(null);
  const [vegTotal, setVegTotal] = useState('');
  const [kidsTotal, setKidsTotal] = useState('');

  // ✅ מודאל מספרים כפולים
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);

  const isSeparated = useMemo(() => invites?.some(i => i.is_separated_event), [invites]);

  const askKids = useMemo(() => {
    const any = invites.find(i => typeof i.ask_kids !== 'undefined');
    return typeof any?.ask_kids === 'boolean' ? any.ask_kids : true;
  }, [invites]);

  const askVegetarian = useMemo(() => {
    const any = invites.find(i => typeof i.ask_vegetarian !== 'undefined');
    return typeof any?.ask_vegetarian === 'boolean' ? any.ask_vegetarian : true;
  }, [invites]);

  const askDrive = useMemo(() => {
    const any = invites.find(i => typeof i.ask_drive !== 'undefined');
    return typeof any?.ask_drive === 'boolean' ? any.ask_drive : false;
  }, [invites]);

  const relationshipOptions = [
    { value: 'חברים חתן', label: 'חברים חתן' },
    { value: 'חברים כלה', label: 'חברים כלה' },
    { value: 'חברים הורים', label: 'חברים הורים' },
    { value: 'משפחה חתן', label: 'משפחה חתן' },
    { value: 'משפחה כלה', label: 'משפחה כלה' },
    { value: 'אחר', label: 'אחר' }
  ];

  // =========================
  // Helpers: Phone + Language
  // =========================

  const sanitizePhoneInput = (raw) => {
    const s = String(raw ?? '').trim();

    if (s.startsWith('+')) {
      const rest = s.slice(1).replace(/[^\d\s\-()]/g, '');
      return '+' + rest;
    }

    if (s.startsWith('00')) {
      return '00' + s.slice(2).replace(/[^\d\s\-()]/g, '');
    }

    return s.replace(/[^\d\s\-()]/g, '');
  };

  const formatPhone = (p) => {
    const s = String(p || '').trim();
    if (!s) return '';
    if (s.startsWith('+')) return s;
    if (s.startsWith('00')) return s;
    if (s.startsWith('0')) return s;

    const digitsOnly = s.replace(/[^\d]/g, '');
    if (digitsOnly && digitsOnly === s && digitsOnly.length <= 10) {
      return `0${digitsOnly}`;
    }

    return s;
  };

  const getInvitePhoneForEdit = (invite) => {
    return invite?.phone_e164 || invite?.phon_number || '';
  };

  const getInviteLangForEdit = (invite) => {
    const v = invite?.preferred_language ?? invite?.language ?? invite?.invite_language;
    return v ? String(v) : 'auto';
  };

  const getInviteLangForLink = (invite) => {
    const v = invite?.preferred_language ?? invite?.language ?? invite?.invite_language;
    const lang = (v ? String(v) : 'auto').toLowerCase();
    return lang;
  };

  const buildInviteConfirmUrl = (invite) => {
    const slug = invite?.invitation_url;
    const base = 'https://mariage-il.com/invitation/confirm';

    const lang = getInviteLangForLink(invite);
    if (lang === 'fr' || lang === 'en') {
      return `${base}/${lang}/${slug}/`;
    }
    return `${base}/${slug}/`;
  };

  // =========================
  // Duplicates helpers
  // =========================
  const normalizePhoneKey = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return '';

    if (s.startsWith('+')) {
      const digits = s.slice(1).replace(/[^\d]/g, '');
      return digits ? `+${digits}` : '';
    }

    if (s.startsWith('00')) {
      const digits = s.slice(2).replace(/[^\d]/g, '');
      return digits ? `+${digits}` : '';
    }

    const digits = s.replace(/[^\d]/g, '');
    if (!digits) return '';

    if (digits.length === 9 && !digits.startsWith('0')) {
      return `0${digits}`;
    }

    return digits;
  };

  const duplicatePhones = useMemo(() => {
    const map = new Map();

    for (const inv of invites) {
      const key = normalizePhoneKey(inv?.phone_e164 || inv?.phon_number);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(inv);
    }

    return [...map.entries()]
      .filter(([, list]) => list.length > 1)
      .sort((a, b) => a[0].localeCompare(b[0], 'en'));
  }, [invites]);

  useEffect(() => { get_invites(); }, [user]);

  const get_invites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${SERVER_URL}get_invites`, {
        headers: { Authorization: `Token ${token}` },
      });
      setInvites(response.data);
      setFilteredInvites(response.data);
    } catch (error) {
      console.error('Error getting invites', error);
    }
  };

  const downloadData = async () => {
    try {
      const response = await axios.get(`${SERVER_URL}export-invites/`, {
        params: { user: user.user },
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'invites.xlsx');
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      console.error(`Error downloading invites.xlsx`, error);
    }
  };

  const handleSearch = () => {
    const filteredResults = invites.filter((invite) =>
      String(invite.name || '').toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredInvites(filteredResults);
  };

  const handleEditClick = (invite) => {
    setCurrentInvite(invite);

    setNewName(invite.name || '');
    setNewPhone(getInvitePhoneForEdit(invite));
    setNewArriving(invite.arriving ?? '');
    setNewRelationship(invite.relationship || '');
    setNewBoysCount(invite.boys_count ?? '');
    setNewGirlsCount(invite.girls_count ?? '');
    setNewPreferredLanguage(getInviteLangForEdit(invite));

    setShowEditModal(true);
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setCurrentInvite(null);
  };

  const validateCoreBeforeSave = () => {
    if (!currentInvite) return false;

    if (currentInvite.is_separated_event) {
      const total = parseInt(String(newArriving || 0), 10) || 0;
      const b = parseInt(String(newBoysCount || 0), 10) || 0;
      const g = parseInt(String(newGirlsCount || 0), 10) || 0;
      if (total > 0 && b === 0 && g === 0) { alert('יש להזין לפחות מספר בנים או מספר בנות'); return false; }
      if (b + g !== total) { alert('סה״כ בנים ובנות חייב להיות שווה למספר הכולל'); return false; }
    }

    return true;
  };

  const handleSaveCore = async () => {
    if (!validateCoreBeforeSave()) return;

    try {
      const payload = {
        invite: currentInvite.id,
        phone: sanitizePhoneInput(newPhone),
        name: newName,
        arriving: newArriving,
        relationship: newRelationship,
        preferred_language: newPreferredLanguage === 'auto' ? null : newPreferredLanguage,
      };

      if (currentInvite.is_separated_event) {
        payload.boys_count = newBoysCount || 0;
        payload.girls_count = newGirlsCount || 0;
      }

      await axios.post(`${SERVER_URL}change_invite`, payload, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      });

      setShowEditModal(false);
      setCurrentInvite(null);
      get_invites();
    } catch (error) {
      console.error('Error changing invite', error);
    }
  };

  useEffect(() => {
    const filtered = invites.filter((invite) => {
      const typeMatches = typeFilter ? invite.relationship === typeFilter : true;

      const arrivingMatches =
        arrivingFilter === '0'
          ? (invite.arriving) === 0
          : arrivingFilter === '>0'
          ? (invite.arriving) > 0
          : arrivingFilter === 'null'
          ? invite.arriving == null
          : true;

      const genderMatches =
        isSeparated && genderFilter
          ? genderFilter === 'boys'
            ? (Number(invite.boys_count) || 0) > 0
            : genderFilter === 'girls'
            ? (Number(invite.girls_count) || 0) > 0
            : true
          : true;

      const nameMatches =
        searchQuery.trim() === ''
          ? true
          : String(invite.name || '').toLowerCase().includes(searchQuery.toLowerCase());

      return typeMatches && arrivingMatches && genderMatches && nameMatches;
    });

    setFilteredInvites(filtered);
  }, [typeFilter, arrivingFilter, genderFilter, searchQuery, invites, isSeparated]);

  const handleRemoveInvite = async (invite) => {
    if (window.confirm(`האם אתה בטוח שברצונך למחוק את ${invite.name} מרשימת המוזמנים?`)) {
      try {
        await axios.delete(`${SERVER_URL}remove_invites`, {
          params: { invite: invite.id },
          headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        });
        get_invites();
      } catch (error) {
        console.error('Error removing invite', error);
      }
    }
  };

  const copyToClipboard = (url, index) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const calculateInviteStats = () => {
    const arrivingGreaterThanZero = invites.filter((i) => Number(i.arriving) > 0).length;
    const arrivingEqualZero = invites.filter(i => i.arriving === 0).length;
    const arrivingUndefined = invites.filter((i) => i.arriving == null).length;
    const howMany = invites.reduce((sum, invite) => sum + (Number(invite.arriving) || 0), 0);
    const boysTotal = invites.reduce((sum, i) => sum + (Number(i.boys_count) || 0), 0);
    const girlsTotal = invites.reduce((sum, i) => sum + (Number(i.girls_count) || 0), 0);
    return {
      total: invites.length,
      arrivingGreaterThanZero,
      arrivingEqualZero,
      arrivingUndefined,
      howMany,
      boysTotal,
      girlsTotal,
    };
  };

  const stats = calculateInviteStats();

  const handleCheckboxChange = (id) => {
    setEditableFields((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const byApprovalDesc = (a, b) => {
    const aHas = !!a.approval_datetime;
    const bHas = !!b.approval_datetime;
    if (aHas && bHas) return new Date(b.approval_datetime) - new Date(a.approval_datetime);
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return 0;
  };

  const formatApproval = (iso) =>
    iso ? new Date(iso).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const sortedFilteredInvites = useMemo(
    () => [...filteredInvites].sort(byApprovalDesc),
    [filteredInvites]
  );

  const peopleCount = (inv) => {
    const boys = Number(inv.boys_count || 0);
    const girls = Number(inv.girls_count || 0);
    if (inv.is_separated_event) return boys + girls;
    return Number(inv.arriving || 0);
  };

  // =========================
  // ✅ DRIVE: קביעה אם להציג "מאיפה" לפי shuttle_locations (לא לפי מה שנבחר)
  // =========================

  // לוקחים מהשרת את כל ה-shuttle_locations של הפרופיל (אם קיימים)
  const shuttleLocationsAll = useMemo(() => {
    const first = invites?.find(Boolean);
    const arr = first?.shuttle_locations;
    return Array.isArray(arr) ? arr : [];
  }, [invites]);

  const showDriveFrom = useMemo(() => {
    // אם יש יותר ממיקום אחד באירוע -> מציגים "כן מ..."
    return shuttleLocationsAll.length > 1;
  }, [shuttleLocationsAll]);

  const driveCellText = (inv) => {
    // הדרך החדשה: יש FK לבחירה
    if (inv?.shuttle_location_id) {
      if (showDriveFrom) {
        const name = String(inv?.shuttle_location_name || '').trim();
        return `כן מ${name || '—'}`;
      }
      return 'כן';
    }
    // אין יותר "אולי" ולא מציגים legacy; אם אין בחירה => ריק
    return '';
  };

  // =========================
  // Specials (ילדים/צמחוני/הסעה)
  // =========================

  const specials = useMemo(() => {
    return invites
      .map(inv => {
        const veg = (
          Number(inv.vegetarian_count || 0) +
          Number(inv.veg_boys_count || 0) +
          Number(inv.veg_girls_count || 0)
        );
        const kids = (
          Number(inv.kids_count || 0) +
          Number(inv.kids_boys_count || 0) +
          Number(inv.kids_girls_count || 0)
        );
        return { ...inv, _vegTotal: veg, _kidsTotal: kids };
      })
      .filter(inv => {
        if (!askVegetarian && !askKids && !askDrive) return false;

        const hasVeg = askVegetarian ? inv._vegTotal > 0 : false;
        const hasKids = askKids ? inv._kidsTotal > 0 : false;

        // ✅ DRIVE: רק חדש (אין אולי)
        const hasDrive = askDrive ? !!inv.shuttle_location_id : false;

        return hasVeg || hasKids || hasDrive;
      })
      .sort(byApprovalDesc);
  }, [invites, askKids, askVegetarian, askDrive]);

  const specialsTotals = useMemo(() => {
    const vegTotalAll = askVegetarian
      ? specials.reduce((s, i) => s + (Number(i._vegTotal) || 0), 0)
      : 0;

    const kidsTotalAll = askKids
      ? specials.reduce((s, i) => s + (Number(i._kidsTotal) || 0), 0)
      : 0;

    let driveYesTotal = 0;

    // פירוט לפי מקום (רק אם יש >1 מיקומים באירוע)
    const byLocation = new Map(); // name -> total people

    if (askDrive) {
      for (const inv of specials) {
        if (!inv?.shuttle_location_id) continue;

        const pc = peopleCount(inv);
        driveYesTotal += pc;

        const name = String(inv?.shuttle_location_name || '').trim() || '—';
        byLocation.set(name, (byLocation.get(name) || 0) + pc);
      }
    }

    const byLocationArr = [...byLocation.entries()]
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);

    return {
      vegTotal: vegTotalAll,
      kidsTotal: kidsTotalAll,
      driveYes: driveYesTotal,
      driveByLocation: byLocationArr,
    };
  }, [specials, askVegetarian, askKids, askDrive]);

  const openSpecialsEditor = (inv) => {
    const veg =
      Number(inv.vegetarian_count || 0) +
      Number(inv.veg_boys_count || 0) +
      Number(inv.veg_girls_count || 0);
    const kids =
      Number(inv.kids_count || 0) +
      Number(inv.kids_boys_count || 0) +
      Number(inv.kids_girls_count || 0);

    setEditSpecialsInvite(inv);
    setVegTotal(veg ? String(veg) : '');
    setKidsTotal(kids ? String(kids) : '');
  };

  const validateSpecials = () => {
    if (!editSpecialsInvite) return false;

    const totalArriving = Number(editSpecialsInvite.arriving || 0);
    if (totalArriving <= 0) {
      alert('כדי לעדכן מנות צמחוניות/ילדים צריך למלא קודם "כמה יגיעו" למוזמן הזה.');
      return false;
    }

    const v = Number(vegTotal || 0);
    const k = Number(kidsTotal || 0);

    if (askVegetarian && (v < 0 || v > totalArriving)) { alert('מנות צמחוניות בין 0 לסה״כ מגיעים'); return false; }
    if (askKids && (k < 0 || k > totalArriving)) { alert('ילדים בין 0 לסה״כ מגיעים'); return false; }

    return true;
  };

  const saveSpecials = async () => {
    if (!validateSpecials()) return;
    try {
      const payload = {
        invite: editSpecialsInvite.id,
        name: editSpecialsInvite.name,
        phone: sanitizePhoneInput(editSpecialsInvite.phon_number),
        arriving: editSpecialsInvite.arriving,
      };
      if (askVegetarian) payload.vegetarian_count = Number(vegTotal || 0);
      if (askKids) payload.kids_count = Number(kidsTotal || 0);

      await axios.post(`${SERVER_URL}change_invite`, payload, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      });

      setEditSpecialsInvite(null);
      get_invites();
    } catch (e) {
      console.error(e?.response?.data || e);
      alert('שמירה נכשלה. ודאי שמולא "כמה יגיעו" ושערכי הילדים/מנות בתקין.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <button onClick={() => setShowSpecialsModal(true)}>בקשות מיוחדות (ילדים/צמחוני/הסעה)</button>
        <button onClick={() => setShowDuplicatesModal(true)}>צפייה במספרים כפולים</button>
      </div>

      {/* ✅ מודאל מספרים כפולים */}
      {showDuplicatesModal && (
        <div className="modal-overlay modal-overlay-duplicates" onClick={() => setShowDuplicatesModal(false)}>
          <div className="modal-panel modal-panel-duplicates" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">מספרים כפולים</h2>
              <button className="modal-close" onClick={() => setShowDuplicatesModal(false)}>
                סגור
              </button>
            </div>

            <div className="modal-body">
              {duplicatePhones.length ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {duplicatePhones.map(([phoneKey, list]) => (
                    <div
                      key={phoneKey}
                      style={{
                        border: '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 10,
                        padding: 12,
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div>
                          <b>{formatPhone(phoneKey)}</b>
                        </div>
                        <div style={{ opacity: 0.75 }}>כמות: {list.length}</div>
                      </div>

                      <div style={{ marginTop: 8, lineHeight: 1.6 }}>
                        {list.map((i) => i?.name).filter(Boolean).join(', ') || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', opacity: 0.8 }}>
                  אין מספרים כפולים ברשימה 🎉
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={() => setShowDuplicatesModal(false)}>סגור</button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ מודאל בקשות מיוחדות */}
      {showSpecialsModal && (
        <div className="modal-overlay" onClick={() => { setShowSpecialsModal(false); setEditSpecialsInvite(null); }}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">בקשות מיוחדות</h2>
              <button className="modal-close" onClick={() => { setShowSpecialsModal(false); setEditSpecialsInvite(null); }}>
                סגור
              </button>
            </div>

            <div className="modal-body">
              <div className="stats-section" style={{ position: 'static', transform: 'none', maxWidth: 'unset', marginBottom: 12 }}>
                {askVegetarian && (
                  <span style={{ marginInlineStart: 12 }}>
                    סה״כ מנות צמחוניות: <b>{specialsTotals.vegTotal || 0}</b>
                  </span>
                )}

                {askKids && (
                  <span style={{ marginInlineStart: 12 }}>
                    סה״כ ילדים: <b>{specialsTotals.kidsTotal || 0}</b>
                  </span>
                )}

                {askDrive && !showDriveFrom && (
                  <span style={{ marginInlineStart: 12 }}>
                    סה״כ בהסעה: <b>{specialsTotals.driveYes || 0}</b>
                  </span>
                )}

                {askDrive && showDriveFrom && (
                  <span style={{ marginInlineStart: 12 }}>
                    סה״כ בהסעה (פירוט): <b style={{ marginInlineStart: 6 }}>{specialsTotals.driveYes || 0}</b>
                  </span>
                )}

                {askDrive && showDriveFrom && (
                  <div style={{ marginTop: 8, opacity: 0.95 }}>
                    {specialsTotals.driveByLocation?.length ? (
                      <div style={{ display: 'grid', gap: 4 }}>
                        {specialsTotals.driveByLocation.map((row) => (
                          <div key={row.name}>
                            <span>• {row.name}: </span>
                            <b>{row.total || 0}</b>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ opacity: 0.8 }}>אין עדיין בחירות הסעה להצגה.</div>
                    )}
                  </div>
                )}
              </div>

              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>שם</th>
                      <th>טלפון</th>
                      {askVegetarian && <th>מנות צמחוניות</th>}
                      {askKids && <th>ילדים</th>}
                      {askDrive && <th>{showDriveFrom ? 'הסעה (מאיפה)' : 'הסעה'}</th>}
                      <th>עריכה</th>
                    </tr>
                  </thead>

                  <tbody>
                    {specials.length ? (
                      specials.map((inv, idx) => (
                        <tr key={inv.id || idx}>
                          <td>{inv.name}</td>
                          <td>{formatPhone(inv.phon_number)}</td>
                          {askVegetarian && <td>{inv._vegTotal || 0}</td>}
                          {askKids && <td>{inv._kidsTotal || 0}</td>}
                          {askDrive && <td>{driveCellText(inv)}</td>}
                          <td><button onClick={() => openSpecialsEditor(inv)}>ערוך</button></td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={(2 + (askVegetarian ? 1 : 0) + (askKids ? 1 : 0) + (askDrive ? 1 : 0) + 1)}
                          style={{ textAlign: 'center' }}
                        >
                          אין מוזמנים עם בקשות מיוחדות
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {editSpecialsInvite && (
                <div className="modal" onClick={() => setEditSpecialsInvite(null)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <h2>עריכת בקשות מיוחדות</h2>

                    <label>
                      שם
                      <input
                        type="text"
                        value={editSpecialsInvite.name || ''}
                        onChange={(e) => setEditSpecialsInvite({ ...editSpecialsInvite, name: e.target.value })}
                      />
                    </label>

                    <label>
                      טלפון
                      <input
                        type="text"
                        inputMode="tel"
                        value={editSpecialsInvite.phon_number || ''}
                        onChange={(e) =>
                          setEditSpecialsInvite({ ...editSpecialsInvite, phon_number: sanitizePhoneInput(e.target.value) })
                        }
                        placeholder="+33 6 12 34 56 78 / 054-1234567"
                      />
                      <small style={{ display: 'block', opacity: 0.7, marginTop: 6 }}>
                        אפשר להכניס מספר חו״ל עם + או 00                      
                        </small>
                    </label>

                    {askVegetarian && (
                      <label>
                        מנות צמחוניות
                        <input
                          type="number"
                          min="0"
                          value={vegTotal}
                          onChange={(e) => setVegTotal(e.target.value)}
                        />
                      </label>
                    )}

                    {askKids && (
                      <label>
                        ילדים
                        <input
                          type="number"
                          min="0"
                          value={kidsTotal}
                          onChange={(e) => setKidsTotal(e.target.value)}
                        />
                      </label>
                    )}

                    {askDrive && (
                      <div style={{ marginTop: 8 }}>
                        הסעה: {driveCellText(editSpecialsInvite) || '—'}
                      </div>
                    )}

                    <div className="modal-actions">
                      <button onClick={saveSpecials}>שמירה</button>
                      <button onClick={() => setEditSpecialsInvite(null)}>בטל</button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button onClick={() => { setShowSpecialsModal(false); setEditSpecialsInvite(null); }}>סגור</button>
            </div>
          </div>
        </div>
      )}

      <div className="page-container">
        <div className="stats-section">
          <label>סה"כ מוזמנים: <span className="highlight">{stats.total}</span></label>
          <label>אישרו הגעה: <span className="highlight">{stats.arrivingGreaterThanZero}</span></label>
          <label>סה"כ אורחים צפויים: <span className="highlight" style={{ color: 'green' }}>{stats.howMany}</span></label>
          <label>לא מגיעים: <span className="highlight" style={{ color: 'red' }}>{stats.arrivingEqualZero}</span></label>
          <label>לא ידוע: <span className="highlight">{stats.arrivingUndefined}</span></label>
          {isSeparated && (
            <>
              <label>סה״כ בנים: <span className="highlight">{stats.boysTotal}</span></label>
              <label>סה״כ בנות: <span className="highlight">{stats.girlsTotal}</span></label>
            </>
          )}
        </div>

        <div className="filters-section">
          <label>
            סנן לפי סוג
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">הכל</option>
              <option value="חברים חתן">חברים חתן</option>
              <option value="חברים כלה">חברים כלה</option>
              <option value="חברים הורים">חברים הורים</option>
              <option value="משפחה חתן">משפחה חתן</option>
              <option value="משפחה כלה">משפחה כלה</option>
              <option value="אחר">אחר</option>
            </select>
          </label>

          <label>
            סנן לפי הגעה
            <select value={arrivingFilter} onChange={(e) => setArrivingFilter(e.target.value)}>
              <option value="">הכל</option>
              <option value="0">לא מגיעים</option>
              <option value=">0">מגיעים</option>
              <option value="null">לא ידוע</option>
            </select>
          </label>

          {isSeparated && (
            <label>
              סנן לפי מגדר
              <select value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                <option value="">הכל</option>
                <option value="boys">בנים (&gt;0)</option>
                <option value="girls">בנות (&gt;0)</option>
              </select>
            </label>
          )}
        </div>

        <div className="download-section">
          <button onClick={downloadData}>הורד כקובץ excel</button>
        </div>

        <div className="search-section">
          <label>
            חפש לפי שם:
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="הכנס שם לחיפוש"
            />
          </label>
          <button onClick={handleSearch}>חפש</button>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>שם</th>
              <th>טלפון</th>
              <th>כמה יגיעו</th>
              {isSeparated && <th>בנים</th>}
              {isSeparated && <th>בנות</th>}
              <th>מספר שולחן</th>
              <th>הערה</th>
              <th>לינק אישי לאישור הגעה</th>
              <th>אושר בתאריך</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {sortedFilteredInvites.length > 0 ? (
              sortedFilteredInvites.map((invite, index) => (
                <tr key={index}>
                  <td data-label="שם">{invite.name}</td>

                  <td data-label="טלפון">
                    {formatPhone(invite.phone_e164 || invite.phon_number)}
                  </td>

                  <td data-label="כמה יגיעו">{invite.arriving ?? '—'}</td>
                  {isSeparated && <td data-label="בנים">{invite.boys_count ?? '-'}</td>}
                  {isSeparated && <td data-label="בנות">{invite.girls_count ?? '-'}</td>}
                  <td data-label="מספר שולחן">{invite.table_numbers_str ?? invite.table_number ?? '—'}</td>
                  <td data-label="הערה">{invite.command || ''}</td>

                  <td>
                    <button onClick={() => copyToClipboard(buildInviteConfirmUrl(invite), index)}>
                      {copiedIndex === index ? 'הועתק!' : 'העתק'}
                    </button>
                  </td>

                  <td data-label="אושר בתאריך">{formatApproval(invite.approval_datetime)}</td>

                  <td>
                    <input
                      type="checkbox"
                      checked={!!editableFields[invite.id]}
                      onChange={() => handleCheckboxChange(invite.id)}
                    />
                    {editableFields[invite.id] && (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => handleEditClick(invite)}>ערוך</button>
                        <button onClick={() => handleRemoveInvite(invite)}>מחק</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr />
            )}
          </tbody>
        </table>
      </div>

      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>עריכה</h2>

            <label>
              שם
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </label>

            <label>
              טלפון
              <input
                type="text"
                inputMode="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(sanitizePhoneInput(e.target.value))}
                placeholder="+33 6 12 34 56 78 / 054-1234567"
              />
              <small style={{ display: 'block', opacity: 0.7, marginTop: 6 }}>
                תומך במספרי חו״ל עם + (או 00). אם זה מספר ישראלי רגיל אפשר להשאיר כרגיל.
              </small>
            </label>

            <label>
              שפת שליחה (למוזמן הזה)
              <select
                value={newPreferredLanguage}
                onChange={(e) => setNewPreferredLanguage(e.target.value)}
              >
                <option value="auto">אוטומטי (ברירת מחדל)</option>
                <option value="he">עברית</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
              <small style={{ display: 'block', opacity: 0.7, marginTop: 6 }}>
                זה רלוונטי רק אם יש לך אורחים מחו״ל. אחרת השאירו “אוטומטי”.
              </small>
            </label>

            <label>
              כמה יגיעו
              <input type="number" value={newArriving} onChange={(e) => setNewArriving(e.target.value)} />
            </label>

            {currentInvite?.is_separated_event && (
              <>
                <label>
                  כמה בנים
                  <input
                    type="number"
                    min="0"
                    value={newBoysCount}
                    onChange={(e) => setNewBoysCount(e.target.value)}
                  />
                </label>
                <label>
                  כמה בנות
                  <input
                    type="number"
                    min="0"
                    value={newGirlsCount}
                    onChange={(e) => setNewGirlsCount(e.target.value)}
                  />
                </label>
              </>
            )}

            <label>
              קשר
              <select value={newRelationship} onChange={(e) => setNewRelationship(e.target.value)}>
                {relationshipOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>

            <div className="modal-actions">
              <button onClick={handleSaveCore}>אישור</button>
              <button onClick={handleCancelEdit}>בטל</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invites;
